import {
  AttachMoney as AttachMoneyIcon,
  DirectionsCar as DirectionsCarIcon,
  Edit as EditIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Place as PlaceIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import {
  Box,
  Chip,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import type { Lead } from '@otw/models/leads/lead';
import React from 'react';
import { salesReps } from '@/constants';
import { getLeadTruckDropoffStatus } from '../../util/leadDropoffValidation';

export interface StatusOption {
  id: string;
  label: string;
  color: string;
}

export interface LeadInfoCardProps {
  lead: Lead;
  columnLabel: string;
  columnColor: string;
  statusOptions: StatusOption[];
  onClick?: (lead: Lead) => void;
  onEdit?: (lead: Lead) => void;
  onStatusChange?: (lead: Lead, newStatus: string, newStatusLabel: string) => void;
  isUpdatingStatus?: boolean;
}

const PRIORITY_COLOR: Record<Lead['priority'], string> = {
  low: '#9e9e9e',
  medium: '#2196f3',
  high: '#f44336',
};

const dateFormatter = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
});

const formatDate = (date: Date | null | undefined): string => {
  if (!date) return '—';
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return '—';
  return dateFormatter.format(d);
};

const getAssignedRepName = (assignedRepId: Lead['assignedRepId']): string => {
  if (assignedRepId === undefined || assignedRepId === null) return 'Unassigned';
  const rep = salesReps.find(
    (candidate) => String(candidate.id) === String(assignedRepId),
  );
  return rep ? `${rep.first_name} ${rep.last_name}` : 'Unknown';
};

/** Best-effort one-line summary of the first truck the customer requested. */
const summarizeTruckRequest = (lead: Lead): string => {
  const first = lead.truckRequest?.types?.[0];
  if (!first) return '';
  const yearPart = first.minYear ? `${first.minYear}+` : '';
  const makePart = Array.isArray(first.make)
    ? first.make.join(' / ')
    : (first.make as string | undefined);
  const modelPart = Array.isArray(first.model)
    ? first.model.join(' / ')
    : (first.model as string | undefined);
  const typePart = Array.isArray(first.type)
    ? first.type.join(' / ')
    : (first.type as string | undefined);
  const sizePart = Array.isArray(first.size)
    ? first.size.join(' / ')
    : (first.size as string | undefined);
  const main = [yearPart, makePart, modelPart].filter(Boolean).join(' ').trim();
  if (main) return main;
  return [typePart, sizePart].filter(Boolean).join(' · ').trim();
};

const LeadInfoCard: React.FC<LeadInfoCardProps> = ({
  lead,
  columnLabel,
  columnColor,
  statusOptions,
  onClick,
  onEdit,
  onStatusChange,
  isUpdatingStatus = false,
}) => {
  const theme = useTheme();
  const priorityColor = PRIORITY_COLOR[lead.priority];
  const truckCount = lead.truckIds?.length ?? 0;
  const truckDropoffStatus = getLeadTruckDropoffStatus(lead);
  const isInteractive = Boolean(onClick);
  const statusValue = lead.status ?? '';
  const truckSummary = summarizeTruckRequest(lead);
  const statusSelectAccent = columnColor;

  return (
    <Paper
      variant="outlined"
      onClick={onClick ? () => onClick(lead) : undefined}
      role={isInteractive ? 'button' : undefined}
      tabIndex={isInteractive ? 0 : undefined}
      onKeyDown={
        isInteractive
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick?.(lead);
              }
            }
          : undefined
      }
      sx={{
        p: 2,
        borderRadius: 2,
        borderLeft: `4px solid ${columnColor}`,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 1.25,
        cursor: isInteractive ? 'pointer' : 'default',
        transition: 'box-shadow 150ms ease, border-color 150ms ease',
        '&:hover': isInteractive
          ? {
              boxShadow: `0 6px 18px ${alpha(columnColor, 0.18)}`,
              borderColor: alpha(columnColor, 0.55),
            }
          : undefined,
        '&:focus-visible': isInteractive
          ? { outline: `2px solid ${alpha(columnColor, 0.6)}`, outlineOffset: 2 }
          : undefined,
      }}
    >
      <Stack
        direction="row"
        alignItems="flex-start"
        justifyContent="space-between"
        spacing={1}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            variant="subtitle2"
            fontWeight={800}
            sx={{ lineHeight: 1.2 }}
            noWrap
            title={lead.company || lead.customerName}
          >
            {lead.company?.trim() || lead.customerName || 'Lead'}
          </Typography>
          {lead.customerName && lead.company?.trim() && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block' }}
              noWrap
            >
              {lead.customerName}
            </Typography>
          )}
        </Box>
        <Stack
          direction="row"
          alignItems="flex-start"
          spacing={0.5}
          sx={{ flexShrink: 0 }}
        >
          {lead.leadNumber?.trim() ? (
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                color: columnColor,
                letterSpacing: 0.4,
                whiteSpace: 'nowrap',
                pt: 0.5,
              }}
              title={`Lead # ${lead.leadNumber.trim()}`}
            >
              {lead.leadNumber.trim()}
            </Typography>
          ) : null}
          {onEdit ? (
            <Tooltip title="Edit lead">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit(lead);
                }}
                aria-label="Edit lead"
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : null}
        </Stack>
      </Stack>

      {onStatusChange ? (
        <TextField
          select
          size="small"
          label="Status"
          value={statusValue}
          disabled={isUpdatingStatus || statusOptions.length === 0}
          onChange={(e) => {
            const next = e.target.value;
            if (next === statusValue) return;
            const opt = statusOptions.find((o) => o.id === next);
            onStatusChange(lead, next, opt?.label ?? next);
          }}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
          SelectProps={{
            MenuProps: {
              onClick: (e) => e.stopPropagation(),
            },
            renderValue: (value) => {
              const opt = statusOptions.find((o) => o.id === value);
              const label = opt?.label ?? columnLabel;
              const color = opt?.color ?? statusSelectAccent;
              return (
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      bgcolor: color,
                      flexShrink: 0,
                    }}
                  />
                  <Typography variant="body2" fontWeight={700} noWrap>
                    {label}
                  </Typography>
                </Stack>
              );
            },
          }}
          InputProps={{
            sx: {
              borderRadius: 2,
              bgcolor: alpha(statusSelectAccent, 0.06),
            },
          }}
          sx={{
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: alpha(statusSelectAccent, 0.4),
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: alpha(statusSelectAccent, 0.7),
            },
          }}
        >
          {statusOptions.map((opt) => (
            <MenuItem key={opt.id} value={opt.id} sx={{ fontSize: 14 }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    bgcolor: opt.color,
                  }}
                />
                <Typography variant="body2" fontWeight={600}>
                  {opt.label}
                </Typography>
              </Stack>
            </MenuItem>
          ))}
        </TextField>
      ) : (
        <Chip
          size="small"
          label={columnLabel}
          variant="outlined"
          sx={{
            alignSelf: 'flex-start',
            height: 22,
            fontWeight: 700,
            borderLeft: `3px solid ${columnColor}`,
            bgcolor: alpha(columnColor, 0.08),
            borderColor: alpha(columnColor, 0.35),
            '& .MuiChip-label': { px: 0.75 },
          }}
        />
      )}

      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ rowGap: 0.5 }}>
        <Chip
          size="small"
          label={`Priority: ${lead.priority}`}
          sx={{
            height: 20,
            fontSize: 11,
            fontWeight: 600,
            textTransform: 'capitalize',
            bgcolor: alpha(priorityColor, 0.12),
            color: priorityColor,
            '& .MuiChip-label': { px: 0.75 },
          }}
        />
        <Chip
          size="small"
          icon={<DirectionsCarIcon sx={{ fontSize: 14 }} />}
          label={`${truckCount} truck${truckCount === 1 ? '' : 's'}`}
          sx={{
            height: 20,
            fontSize: 11,
            fontWeight: 600,
            bgcolor: alpha(theme.palette.info.main, 0.12),
            color: theme.palette.info.dark,
            '& .MuiChip-icon': { color: 'inherit', ml: 0.5 },
            '& .MuiChip-label': { px: 0.75 },
          }}
        />
        {lead.source && (
          <Chip
            size="small"
            label={`Source: ${lead.source}`}
            sx={{
              height: 20,
              fontSize: 11,
              fontWeight: 600,
              bgcolor: alpha(theme.palette.text.secondary, 0.08),
              color: 'text.secondary',
              '& .MuiChip-label': { px: 0.75 },
            }}
          />
        )}
        {lead.timeFrame && (
          <Chip
            size="small"
            icon={<ScheduleIcon sx={{ fontSize: 14 }} />}
            label={lead.timeFrame}
            sx={{
              height: 20,
              fontSize: 11,
              fontWeight: 600,
              bgcolor: alpha(theme.palette.warning.main, 0.12),
              color: theme.palette.warning.dark,
              '& .MuiChip-icon': { color: 'inherit', ml: 0.5 },
              '& .MuiChip-label': { px: 0.75 },
            }}
          />
        )}
        {lead.financingStatus && (
          <Chip
            size="small"
            icon={<AttachMoneyIcon sx={{ fontSize: 14 }} />}
            label={lead.financingStatus}
            sx={{
              height: 20,
              fontSize: 11,
              fontWeight: 600,
              bgcolor: alpha(theme.palette.success.main, 0.12),
              color: theme.palette.success.dark,
              '& .MuiChip-icon': { color: 'inherit', ml: 0.5 },
              '& .MuiChip-label': { px: 0.75 },
            }}
          />
        )}
      </Stack>

      {(truckSummary || lead.vehicleInterest) && (
        <Box>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', lineHeight: 1.1 }}
          >
            Looking for
          </Typography>
          <Typography
            variant="body2"
            fontWeight={600}
            sx={{ wordBreak: 'break-word' }}
          >
            {truckSummary || lead.vehicleInterest}
          </Typography>
          {truckSummary && lead.vehicleInterest && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mt: 0.25, wordBreak: 'break-word' }}
            >
              {lead.vehicleInterest}
            </Typography>
          )}
        </Box>
      )}

      {(lead.email || lead.phone) && (
        <Stack direction="row" spacing={1.25} flexWrap="wrap" useFlexGap sx={{ rowGap: 0.25 }}>
          {lead.email && (
            <Stack
              direction="row"
              spacing={0.5}
              alignItems="center"
              sx={{ color: 'text.secondary', minWidth: 0 }}
            >
              <EmailIcon sx={{ fontSize: 14 }} />
              <Typography variant="caption" noWrap title={lead.email}>
                {lead.email}
              </Typography>
            </Stack>
          )}
          {lead.phone && (
            <Stack
              direction="row"
              spacing={0.5}
              alignItems="center"
              sx={{ color: 'text.secondary' }}
            >
              <PhoneIcon sx={{ fontSize: 14 }} />
              <Typography variant="caption" noWrap>
                {lead.phone}
              </Typography>
            </Stack>
          )}
        </Stack>
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
          rowGap: 1,
          columnGap: 1,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.1 }}>
            Assigned rep
          </Typography>
          <Typography variant="body2" fontWeight={600} noWrap>
            {getAssignedRepName(lead.assignedRepId)}
          </Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.1 }}>
            Updated
          </Typography>
          <Typography variant="body2" fontWeight={600}>
            {formatDate(lead.updatedAt)}
          </Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.1 }}>
            Created
          </Typography>
          <Typography variant="body2" fontWeight={600}>
            {formatDate(lead.createdAt)}
          </Typography>
        </Box>
        {lead.lastContactDate && (
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.1 }}>
              Last contact
            </Typography>
            <Typography variant="body2" fontWeight={600}>
              {formatDate(lead.lastContactDate)}
            </Typography>
          </Box>
        )}
      </Box>

      {truckDropoffStatus.total > 0 && (
        <Stack
          direction="row"
          spacing={0.75}
          alignItems="flex-start"
          sx={{ color: 'text.secondary' }}
        >
          <PlaceIcon fontSize="small" sx={{ mt: 0.25 }} />
          <Typography variant="caption" sx={{ wordBreak: 'break-word' }}>
            Dropoff: {truckDropoffStatus.assigned}/{truckDropoffStatus.total} trucks
          </Typography>
        </Stack>
      )}
    </Paper>
  );
};

export default LeadInfoCard;
